import React, { useCallback, useEffect, useMemo, useState } from "react";

import { Map as MapIcon } from "lucide-react";

import Button from "../../Components/Button";
import ToggleSwitch from "../../Components/ToggleSwitch";
import { useAppDispatch, useAppSelector } from "../../Store/Hooks";
import {
  fetchAdminPortfolioMapsConfig,
  updateAdminPortfolioMapsConfig,
} from "../../Store/Reducers/AdminSlice";
import {
  defaultPortfolioMapsConfig,
  normalizePortfolioMapsConfig,
  PORTFOLIO_MAP_LENS_OPTIONS,
  type PortfolioMapLensKey,
  type PortfolioMapLensVisibility,
  type PortfolioMapsConfig,
} from "../../Utils/Constants/portfolioMapLenses";
import "../AdminCronSettingsContainer/style.scss";

const CATEGORIES = ["Business Health", "Account Health", "Both"] as const;

const AdminPortfolioMapsContainer: React.FC = () => {
  const dispatch = useAppDispatch();
  const {
    config: savedConfig,
    loading: fetching,
    saving,
    loadError,
  } = useAppSelector(state => state.admin.portfolioMapsSettings);

  const [enabled, setEnabled] = useState(false);
  const [lenses, setLenses] = useState<PortfolioMapLensVisibility>(
    () => defaultPortfolioMapsConfig().lenses
  );

  const applyConfig = useCallback((raw: unknown) => {
    const next = normalizePortfolioMapsConfig(raw);
    setEnabled(next.enabled);
    setLenses(next.lenses);
  }, []);

  const loadSettings = useCallback(async () => {
    const result = await dispatch(fetchAdminPortfolioMapsConfig());
    if (fetchAdminPortfolioMapsConfig.fulfilled.match(result)) {
      applyConfig(result.payload);
    }
  }, [applyConfig, dispatch]);

  useEffect(() => {
    void loadSettings();
  }, [loadSettings]);

  useEffect(() => {
    if (savedConfig) applyConfig(savedConfig);
  }, [savedConfig, applyConfig]);

  const draft: PortfolioMapsConfig = useMemo(
    () => ({ enabled, lenses }),
    [enabled, lenses]
  );

  const isUnchanged = useMemo(() => {
    if (!savedConfig) return false;
    const normalizedSaved = normalizePortfolioMapsConfig(savedConfig);
    return JSON.stringify(normalizedSaved) === JSON.stringify(draft);
  }, [draft, savedConfig]);

  const setLens = (key: PortfolioMapLensKey, value: boolean) => {
    setLenses(prev => ({ ...prev, [key]: value }));
  };

  const handleSave = async () => {
    const result = await dispatch(updateAdminPortfolioMapsConfig(draft));
    if (updateAdminPortfolioMapsConfig.fulfilled.match(result)) {
      applyConfig(result.payload);
    }
  };

  const loading = fetching && !savedConfig && !loadError;

  return (
    <div className="admin-cron-settings">
      <div className="admin-cron-settings__header">
        <div className="admin-cron-settings__icon">
          <MapIcon size={20} />
        </div>
        <div>
          <h2 className="admin-cron-settings__title">Portfolio Maps</h2>
          <p className="admin-cron-settings__subtitle">
            Global visibility for My Accounts → Maps. Applies to every
            organization.
          </p>
        </div>
      </div>

      {loadError ? (
        <div className="admin-cron-settings__banner" role="alert">
          <p>{loadError}</p>
          <Button variant="outline-primary" onClick={() => void loadSettings()}>
            Retry
          </Button>
        </div>
      ) : (
        <div className="admin-cron-settings__card space-y-4">
          <div className="flex items-center justify-between rounded-md border border-gray-200 bg-gray-50 px-3 py-2.5">
            <div>
              <div className="text-sm font-semibold text-gray-900">
                Portfolio Maps
              </div>
              <div className="text-xs text-gray-500">
                Master switch for the Maps tab
              </div>
            </div>
            <ToggleSwitch
              checked={enabled}
              onChange={setEnabled}
              id="portfolio-maps-enabled-global"
              disabled={loading || saving}
            />
          </div>

          <div
            className={`space-y-4 ${enabled ? "" : "pointer-events-none opacity-50"}`}
          >
            {CATEGORIES.map(category => {
              const options = PORTFOLIO_MAP_LENS_OPTIONS.filter(
                o => o.category === category
              );
              return (
                <div key={category}>
                  <div className="mb-1.5 text-[11px] font-bold uppercase tracking-wide text-gray-400">
                    {category}
                  </div>
                  <div className="space-y-1.5">
                    {options.map(opt => (
                      <label
                        key={opt.key}
                        className="flex cursor-pointer items-center justify-between rounded-md border border-gray-200 bg-white px-3 py-2"
                      >
                        <span className="text-sm font-medium text-gray-800">
                          {opt.label}
                        </span>
                        <ToggleSwitch
                          checked={lenses[opt.key]}
                          onChange={checked => setLens(opt.key, checked)}
                          id={`portfolio-map-lens-global-${opt.key}`}
                          disabled={!enabled || loading || saving}
                        />
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>

          <div className="admin-cron-settings__actions">
            <Button
              variant="primary"
              onClick={() => void handleSave()}
              loading={saving}
              disabled={loading || saving || isUnchanged}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPortfolioMapsContainer;
